"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, ChevronDown, Loader2 } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT } from "@/lib/constants/contact";
import { cn } from "@/lib/utils";
import {
  CONTACT_ERROR_KEYS,
  CONTACT_FORM_DEFAULTS,
  MESSAGE_MAX_LENGTH,
  SERVICE_TYPES,
  contactFormSchema,
  type ContactErrorKey,
  type ContactFormValues,
  type ContactVariant,
} from "@/lib/validators/contact-form";
import { ContactCharacterCounter } from "./contact-character-counter";
import { ContactField } from "./contact-field";
import { ContactSuccessPanel } from "./contact-success-panel";

interface ContactFormProps {
  className?: string;
}

/**
 * The privacy row wires its own ARIA instead of going through `ContactField`,
 * because its copy is a sentence with a link in it rather than a label above a
 * control. Both ends of each association must agree exactly, so the ids are
 * written once.
 *
 * `aria-labelledby` points at the whole sentence, not at the `<label>`: the
 * label deliberately covers only the leading text (so the anchor can sit
 * outside it), and "You agree to our friendly" is not a name anyone can act on.
 * The wrapper's text content restores the full "…friendly privacy policy."
 */
const PRIVACY_ERROR_ID = "contact-agreeToPrivacy-error";
const PRIVACY_LABEL_ID = "contact-agreeToPrivacy-label";

/**
 * Fields in the order they appear on screen, which is the order the summary
 * lists them in.
 *
 * Not `Object.keys(errors)`: that arrives in schema order, and the two differ —
 * `superRefine` issues land after every field issue, so a missing service type
 * would be listed below the message it sits above. A summary whose order does
 * not match the page is a summary the visitor has to re-read.
 */
const SUMMARY_FIELD_ORDER = [
  "firstName",
  "lastName",
  "email",
  "phoneNumber",
  "serviceType",
  "message",
  "agreeToPrivacy",
] as const satisfies readonly (keyof ContactFormValues)[];

/**
 * Move focus to a field by name. `ContactField` and the two checkbox rows all
 * derive their id the same way, so the summary can reach any of them — including
 * the privacy checkbox, which is exactly the control react-hook-form cannot.
 * No `scrollIntoView`: focusing already scrolls, and it honours the visitor's
 * reduced-motion setting, which a smooth scroll would not.
 */
const focusField = (name: string) => {
  document.getElementById(`contact-${name}`)?.focus();
};

/**
 * Zod messages arrive as `string`, so membership is checked at runtime — a
 * type-level union alone would not stop a Zod default from being namespaced and
 * rendered as `contactForm.errors.Invalid enum value…`.
 */
const isContactErrorKey = (key: string): key is ContactErrorKey =>
  (CONTACT_ERROR_KEYS as readonly string[]).includes(key);

/** Shared by every input so the three-step surface ramp stays consistent. */
const CONTROL =
  "h-12 w-full rounded-xl border border-white/10 bg-site-section px-4 text-site-foreground placeholder:text-site-foreground/55 focus-visible:border-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-site-card aria-[invalid=true]:border-red-400";

export function ContactForm({ className }: ContactFormProps) {
  const t = useTranslations("contactForm");
  const [submitted, setSubmitted] = useState<ContactVariant | null>(null);
  const [submitError, setSubmitError] = useState<ReactNode | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting, submitCount },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: CONTACT_FORM_DEFAULTS,
    // Validate on submit, then clear errors as the visitor fixes them. Nobody
    // wants to be told their email is malformed while they are still typing it.
    mode: "onSubmit",
    reValidateMode: "onChange",
    // The summary takes focus instead of the first invalid input. Field focus
    // announces one violation of several, and announces nothing at all when the
    // only invalid control is one react-hook-form holds no ref for — which is
    // the privacy checkbox, since it is controlled through setValue.
    shouldFocusError: false,
  });

  const variant = watch("variant");
  const isCareers = variant === "careers";

  // Untrimmed on purpose: the counter reports what the visitor can see they
  // typed. The schema trims before measuring, so trailing whitespace is the one
  // case where the two differ — and it differs in the visitor's favour.
  const messageLength = watch("message").length;

  // No form *value* is reset when the variant changes — switching is reversible,
  // so every value survives it, including serviceType and isNewCustomer. The
  // guarantee that a stale service type never reaches a job application lives
  // in the payload builder below, where it is unconditional; a state reset
  // would only have cost the visitor their selection on a round trip.
  //
  // The submit banner is the exception, because it is not a value: it describes
  // the send that just failed, and "Service type is required." makes no sense
  // hanging over a job application that has no service type.
  useEffect(() => {
    setSubmitError(null);
  }, [variant]);

  /**
   * Translate a schema message suffix into copy.
   *
   * `messageTooShort` is the one suffix without a same-named key: the ask is
   * worded per variant, because the field is a service description on one path
   * and a cover letter on the other, and "so we can be ready for your service"
   * is wrong on a job application.
   *
   * Anything else is not interpolated. A message the schema does not own — a Zod
   * default from a rule added without one — would otherwise be namespaced and
   * shown verbatim, so an unknown suffix resolves to generic translated copy.
   */
  const messageFor = (key?: string) => {
    if (!key) return undefined;
    if (key === "messageTooShort") {
      return t(
        isCareers
          ? "errors.messageTooShortCareers"
          : "errors.messageTooShortService",
      );
    }
    if (!isContactErrorKey(key)) return t("errors.unexpected");
    return t(`errors.${key}`);
  };

  /**
   * Every violation on screen, in screen order, translated once so the summary
   * and the field message can never word the same problem differently.
   */
  const violations = SUMMARY_FIELD_ORDER.flatMap((name) => {
    const message = messageFor(errors[name]?.message);
    return message ? [{ name, message }] : [];
  });

  // Read through a ref so the effect can depend on submitCount alone. Depending
  // on the violation count instead would re-run as the visitor fixes fields and
  // yank focus out from under them mid-correction.
  const violationCountRef = useRef(0);
  violationCountRef.current = violations.length;

  const summaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitCount > 0 && violationCountRef.current > 0) {
      summaryRef.current?.focus();
    }
  }, [submitCount]);

  const onSubmit = async (values: ContactFormValues) => {
    setSubmitError(null);

    // The two variant-specific fields are neutralized HERE rather than by
    // clearing form state, so the guarantee holds regardless of what the
    // visitor selected and then switched away from. This is the only place that
    // decides what reaches the wire.
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      message: values.message,
      serviceType: values.variant === "service" ? values.serviceType : "",
      isNewCustomer:
        values.variant === "service" ? values.isNewCustomer : false,
      agreeToPrivacy: values.agreeToPrivacy,
      isJobApplication: values.variant === "careers",
    };

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // A 4xx names something the visitor can fix, so the route's own message
        // is more useful than ours. A 5xx is our problem, and the route's copy
        // is English-only and omits the phone number — so use the translated
        // copy, which tells them how else to reach us. A non-JSON body (proxy
        // error page, gateway timeout) lands on the same translated copy.
        const body =
          response.status < 500
            ? ((await response.json().catch(() => null)) as {
                error?: string;
              } | null)
            : null;
        setSubmitError(
          body?.error ||
            t.rich("submitFailed", {
              phone: () => <span data-phone="primary">{CONTACT.phoneWithCountry}</span>,
            }),
        );
        return;
      }

      setSubmitted(values.variant);
      reset(CONTACT_FORM_DEFAULTS);
    } catch {
      // fetch rejects on a network failure with a technical, untranslated
      // message ("Failed to fetch"). Never surface that: the visitor needs the
      // phone number, which is what the translated copy carries. Values are
      // deliberately NOT reset here — a failed send must not cost the visitor
      // everything they typed.
      setSubmitError(
        t.rich("submitFailed", {
          phone: () => <span data-phone="primary">{CONTACT.phoneWithCountry}</span>,
        }),
      );
    }
  };

  if (submitted) {
    return (
      <ContactSuccessPanel
        variant={submitted}
        onReset={() => {
          setSubmitted(null);
          setSubmitError(null);
        }}
      />
    );
  }

  return (
    <div className={cn("rounded-2xl bg-site-card p-5 sm:p-8", className)}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        {/*
          One announcement covering every violation, focused on each failed
          submit. Each entry moves focus to the control it names — a button, not
          a link, because it goes nowhere: it is a focus move, and a link that
          does not navigate lies to anyone reading the page by its links.
        */}
        {violations.length > 0 && (
          <div
            ref={summaryRef}
            tabIndex={-1}
            role="alert"
            className="rounded-xl border border-red-400/40 bg-red-950/40 px-4 py-3 text-sm text-red-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-site-card"
          >
            <p className="flex items-start gap-3 font-medium text-red-100">
              <AlertTriangle
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0"
              />
              {t("errorSummaryTitle", { count: violations.length })}
            </p>
            <ul className="ml-8 mt-2 list-disc space-y-1">
              {violations.map((violation) => (
                <li key={violation.name}>
                  <button
                    type="button"
                    onClick={() => focusField(violation.name)}
                    className="text-left underline underline-offset-4 hover:text-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-site-card"
                  >
                    {violation.message}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/*
          A fieldset + legend + native radios, not Radix Tabs. Tabs unmount the
          inactive panel, which would discard everything typed when the visitor
          switches — and the whole point of asking inline is that they can change
          their mind cheaply. Native radios also give arrow-key navigation and
          group announcement for free.
        */}
        <fieldset>
          <legend className="mb-3 text-sm font-medium text-site-foreground">
            {t("variantLegend")}
          </legend>
          {/*
            A two-column grid rather than a flex pill: es-US labels run ~25%
            longer than en-US, and a grid cell wraps to a second line instead of
            overflowing at 320px.
          */}
          <div className="grid grid-cols-2 gap-1 rounded-2xl bg-site-section p-1">
            <VariantOption
              value="service"
              label={t("variantService")}
              register={register}
            />
            <VariantOption
              value="careers"
              label={t("variantCareers")}
              register={register}
            />
          </div>
        </fieldset>

        <div>
          <h2 className="text-2xl font-semibold text-site-foreground">
            {isCareers ? t("jobApplicationTitle") : t("serviceRequestTitle")}
          </h2>
          <p className="mt-1 text-sm text-site-foreground/70">
            {isCareers ? t("jobApplicationDesc") : t("serviceRequestDesc")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ContactField
            name="firstName"
            label={t("firstName")}
            error={messageFor(errors.firstName?.message)}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                {...register("firstName")}
                id={id}
                type="text"
                autoComplete="given-name"
                aria-invalid={invalid}
                aria-describedby={describedBy}
                className={CONTROL}
              />
            )}
          </ContactField>

          <ContactField
            name="lastName"
            label={t("lastName")}
            optionalLabel={t("optional")}
            error={messageFor(errors.lastName?.message)}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                {...register("lastName")}
                id={id}
                type="text"
                autoComplete="family-name"
                aria-invalid={invalid}
                aria-describedby={describedBy}
                className={CONTROL}
              />
            )}
          </ContactField>
        </div>

        <ContactField
          name="email"
          label={t("email")}
          error={messageFor(errors.email?.message)}
        >
          {({ id, describedBy, invalid }) => (
            <Input
              {...register("email")}
              id={id}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t("emailPlaceholder")}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              className={CONTROL}
            />
          )}
        </ContactField>

        {/*
          Optional on the careers path only, and marked to match — a field
          labelled "(optional)" that then blocks the submit is worse than one
          that was never marked at all.
        */}
        <ContactField
          name="phoneNumber"
          label={t("phoneNumber")}
          {...(isCareers ? { optionalLabel: t("optional") } : {})}
          error={messageFor(errors.phoneNumber?.message)}
        >
          {({ id, describedBy, invalid }) => (
            <Input
              {...register("phoneNumber")}
              id={id}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={t("phonePlaceholder")}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              className={CONTROL}
            />
          )}
        </ContactField>

        {!isCareers && (
          <>
            <ContactField
              name="serviceType"
              label={t("serviceType")}
              error={messageFor(errors.serviceType?.message)}
            >
              {({ id, describedBy, invalid }) => (
                <div className="relative">
                  <select
                    {...register("serviceType")}
                    id={id}
                    aria-invalid={invalid}
                    aria-describedby={describedBy}
                    className={cn(CONTROL, "appearance-none pr-10")}
                  >
                    <option value="">{t("selectService")}</option>
                    {SERVICE_TYPES.map((service) => (
                      <option key={service} value={service}>
                        {t(SERVICE_TYPE_LABEL_KEYS[service])}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    aria-hidden="true"
                    className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-site-foreground/60"
                  />
                </div>
              )}
            </ContactField>

            <label
              htmlFor="contact-isNewCustomer"
              className="flex items-start gap-3 text-sm text-site-foreground/80"
            >
              <Checkbox
                id="contact-isNewCustomer"
                checked={watch("isNewCustomer")}
                onCheckedChange={(checked) =>
                  setValue("isNewCustomer", checked === true)
                }
                className="mt-0.5 h-5 w-5 border-white/25 data-[state=checked]:border-yellow-500 data-[state=checked]:bg-yellow-500"
              />
              {t("newCustomer")}
            </label>
          </>
        )}

        <ContactField
          name="message"
          label={isCareers ? t("coverLetterLabel") : t("messageLabel")}
          help={isCareers ? t("messageHelpCareers") : t("messageHelpService")}
          error={messageFor(errors.message?.message)}
          counter={
            <ContactCharacterCounter
              length={messageLength}
              max={MESSAGE_MAX_LENGTH}
            />
          }
        >
          {({ id, describedBy, invalid }) => (
            <Textarea
              {...register("message")}
              id={id}
              rows={5}
              placeholder={
                isCareers
                  ? t("coverLetterPlaceholder")
                  : t("messagePlaceholder")
              }
              aria-invalid={invalid}
              aria-describedby={describedBy}
              className={cn(CONTROL, "h-auto min-h-[140px] resize-y py-3")}
            />
          )}
        </ContactField>

        <div>
          {/* The policy link is a sibling of the label, not inside it: a label
              may not contain interactive content, and nesting the anchor meant
              opening the policy in a new tab silently toggled the consent box
              behind the reader. The label still covers the sentence's leading
              text, so the checkbox keeps a clickable target and an accessible
              name. */}
          <div className="flex items-start gap-3 text-sm text-site-foreground/80">
            <Checkbox
              id="contact-agreeToPrivacy"
              checked={watch("agreeToPrivacy")}
              onCheckedChange={(checked) =>
                setValue("agreeToPrivacy", checked === true, {
                  shouldValidate: true,
                })
              }
              aria-invalid={Boolean(errors.agreeToPrivacy)}
              aria-labelledby={PRIVACY_LABEL_ID}
              aria-describedby={
                errors.agreeToPrivacy ? PRIVACY_ERROR_ID : undefined
              }
              className="mt-0.5 h-5 w-5 border-white/25 data-[state=checked]:border-yellow-500 data-[state=checked]:bg-yellow-500"
            />
            <span id={PRIVACY_LABEL_ID}>
              <label
                htmlFor="contact-agreeToPrivacy"
                className="cursor-pointer"
              >
                {t("privacyAgree")}
              </label>{" "}
              <Link
                href="/privacy"
                className="text-yellow-500 underline underline-offset-4"
              >
                {t("privacyPolicy")}
              </Link>
              .
            </span>
          </div>
          {errors.agreeToPrivacy && (
            <p id={PRIVACY_ERROR_ID} className="mt-2 text-sm text-red-400">
              {messageFor(errors.agreeToPrivacy.message)}
            </p>
          )}
        </div>

        {/* Above the button, so it is not below the fold on a phone. */}
        {submitError && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-red-400/40 bg-red-950/40 px-4 py-3 text-sm text-red-200"
          >
            <AlertTriangle
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0"
            />
            <span>{submitError}</span>
          </div>
        )}

        {/*
          The reply-time promise belongs before the decision it is meant to
          inform, not only in the confirmation that follows it. It is shown on
          the service path alone: two business hours is what the shop answers a
          service request in, and repeating it over a job application would
          promise something nobody has committed to.
        */}
        {!isCareers && (
          <p className="text-sm text-site-foreground/70">
            {t("responsePromiseService")}
          </p>
        )}

        {/*
          Disabled only while the request is in flight — never for validity.
          A button that is dead because a checkbox is unticked, with nothing
          saying so, is what this redesign exists to remove.
        */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="h-12 w-full rounded-full bg-yellow-500 font-medium text-black transition-colors hover:bg-yellow-600 disabled:opacity-70 motion-reduce:transition-none"
        >
          {isSubmitting && (
            <Loader2
              aria-hidden="true"
              className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
            />
          )}
          {isSubmitting
            ? t("sending")
            : isCareers
              ? t("submitApplication")
              : t("sendMessage")}
        </Button>
      </form>
    </div>
  );
}

/** Service value → its label key, so a new service is a compile error here. */
const SERVICE_TYPE_LABEL_KEYS: Record<
  (typeof SERVICE_TYPES)[number],
  "tireService" | "mechanicService" | "collisionRepair"
> = {
  "tire-service": "tireService",
  "mechanic-service": "mechanicService",
  "collision-repair": "collisionRepair",
};

function VariantOption({
  value,
  label,
  register,
}: {
  value: ContactVariant;
  label: string;
  register: ReturnType<typeof useForm<ContactFormValues>>["register"];
}) {
  return (
    <label className="cursor-pointer">
      <input
        type="radio"
        value={value}
        {...register("variant")}
        className="peer sr-only"
      />
      <span className="flex min-h-12 items-center justify-center rounded-xl px-3 py-2 text-center text-sm font-medium leading-tight text-site-foreground/70 transition-colors hover:text-site-foreground peer-checked:bg-yellow-500 peer-checked:font-semibold peer-checked:text-black peer-focus-visible:ring-2 peer-focus-visible:ring-yellow-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-site-section motion-reduce:transition-none">
        {label}
      </span>
    </label>
  );
}
